import './AddApp.scss';
import { AsyncStateStatus } from '@sonar-software/xuder';
import { createAsyncSelectors } from '@lib/selectors';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { monitorMenuConfigs } from '@security/monitors/monitor-form/MonitorMenu';
import { SonarCard } from '@ui/general';
import { AddAppSettings } from '../app-form';
import { Button, Row } from 'antd';
import { ClassificationLevel, coreActions, DataSecurityApplicationStatus } from '@sonar-software/sonar-core';
import { CONFLICT_MESSAGE, DUPLICATE_MESSAGE } from '../../@arch/validation';
import { COPY_GENERIC, COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { MonitorFormList } from '../app-monitors';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useAppNotifications, useAsyncProcessing, useAsyncState, useDataSecurityApps, useFeatureFlags, useForm, useOnMount, useUpdateDataSecurityApp } from '@hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
export const patchAppSelectors = createAsyncSelectors((state) => state.core.dataSecurity.applications.byId.patch);
const dataSecurityMonitorSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitors.application.get);
function defaultMonitors(isEnabled) {
    return [...monitorMenuConfigs.filter((x) => x.intrinsic &&
            x.active &&
            ((x.featureFlag && isEnabled(x.featureFlag)) || !x.featureFlag) &&
            x.applicationRequired)
            .map((item) => ({
            monitorConfig: {
                monitorTypeId: item.type
            },
            name: item.default.name
        }))];
}
export const AddApp = () => {
    var _a;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const applicationId = Number(searchParams.get('applicationId'));
    const apps = useDataSecurityApps(false, true);
    const app = apps.find((x) => x.applicationId === applicationId);
    const { errorNotification } = useAppNotifications();
    const { data: monitorData } = useAsyncState(dataSecurityMonitorSelectors);
    const usedComboRef = useRef({});
    const monitorFormRef = useRef(null);
    const [monitorsFormIsValid, setMonitorsFormIsValid] = useState(true);
    const { isEnabled } = useFeatureFlags();
    const initialMonitors = useMemo(() => {
        if (!monitorData || monitorData.length === 0) {
            return defaultMonitors(isEnabled);
        }
        const filteredMonitors = monitorData.filter((monitor) => {
            var _a;
            const flag = (_a = monitorMenuConfigs
                .find((config) => config.type === monitor.monitorConfig.monitorTypeId)) === null || _a === void 0 ? void 0 : _a.featureFlag;
            return flag ? isEnabled(flag) : true;
        });
        return filteredMonitors.length > 0 ? filteredMonitors : defaultMonitors(isEnabled);
    }, [isEnabled, monitorData]);
    const loadDataSecurityAppMonitors = useAction(coreActions.requestGetCoreDataSecurityMonitorsByApplication);
    const patchApplication = useUpdateDataSecurityApp();
    const { form: appSettingsForm } = useForm({
        required: ['name']
    });
    useOnMount(() => {
        if (applicationId) {
            loadDataSecurityAppMonitors({ applicationId });
        }
        else {
            navigate(FULL_ROUTES.DATA_SECURITY);
        }
    });
    useEffect(() => {
        if (applicationId && (app === null || app === void 0 ? void 0 : app.name)) {
            appSettingsForm.setFieldsValue({
                description: app.description,
                label: app.label,
                name: app.name
            });
            appSettingsForm.validate();
        }
    }, [app, appSettingsForm, applicationId]);
    const handleReturn = useCallback(() => {
        navigate(FULL_ROUTES.DATA_SECURITY_APPLICATION_SETUP);
        appSettingsForm.reset();
    }, [appSettingsForm, navigate]);
    const onConflict = useCallback(() => {
        var _a, _b;
        const name = (_a = appSettingsForm.getFieldValue('name')) !== null && _a !== void 0 ? _a : '';
        const label = (_b = appSettingsForm.getFieldValue('label')) !== null && _b !== void 0 ? _b : '';
        usedComboRef.current = {
            label,
            name
        };
        appSettingsForm.validateFields(['name', 'label']);
    }, [appSettingsForm]);
    const processPatch = useAsyncProcessing({
        resetAction: patchApplication.reset,
        stateSelector: patchAppSelectors.state,
        statusCodeHandlers: {
            [ErrorOrFailStatusCode.Conflict]: onConflict
        },
        statusHandlers: {
            [AsyncStateStatus.Received]: handleReturn,
            [AsyncStateStatus.Errored]: () => errorNotification(COPY_GENERIC.CONTACT_SUPPORT),
            [AsyncStateStatus.Failed]: () => errorNotification(COPY_GENERIC.CONTACT_SUPPORT)
        }
    });
    const handleOnValuesChange = useCallback((changedValues) => {
        appSettingsForm.validate();
        if (changedValues.name || changedValues.label) {
            const [changedField] = Object.keys(changedValues);
            const [name, label] = appSettingsForm.getFieldsError(['name', 'label']);
            const hasErrors = name.errors.length || label.errors.length;
            appSettingsForm.validateFields(hasErrors ?
                [
                    ...name.errors.length ? ['name'] : [],
                    ...label.errors.length ? ['label'] : []
                ] :
                [changedField]);
        }
    }, [appSettingsForm]);
    const handleOnFinish = useCallback(() => {
        var _a, _b;
        appSettingsForm.validate();
        (_a = monitorFormRef.current) === null || _a === void 0 ? void 0 : _a.validate();
        const { name, user, label, description, owner, stakeholderIds, group, activityPeriod, classification } = appSettingsForm.getFieldsValue();
        const data = {
            activityPeriod,
            classification: classification === ClassificationLevel.None ? null : classification,
            description,
            label,
            name,
            ownerId: owner,
            rawSalesforceId: user,
            stakeholderGroupId: group,
            stakeholderIds
        };
        processPatch.watchNextCall();
        patchApplication.request(Object.assign(Object.assign({}, data), { applicationId, status: DataSecurityApplicationStatus.Added }));
        (_b = monitorFormRef.current) === null || _b === void 0 ? void 0 : _b.saveAllMonitors();
    }, [
        appSettingsForm,
        monitorFormRef,
        processPatch,
        applicationId,
        patchApplication
    ]);
    const validateNameLabelCombination = useCallback(() => {
        var _a, _b;
        const currentName = (_a = appSettingsForm.getFieldValue('name')) !== null && _a !== void 0 ? _a : '';
        const currentLabel = (_b = appSettingsForm.getFieldValue('label')) !== null && _b !== void 0 ? _b : '';
        const { name, label } = usedComboRef.current;
        if (name === currentName && label === currentLabel) {
            return Promise.reject(currentLabel ? CONFLICT_MESSAGE : DUPLICATE_MESSAGE);
        }
        return Promise.resolve();
    }, [appSettingsForm]);
    const handleOnMonitorValuesChange = useCallback(() => {
        var _a;
        setMonitorsFormIsValid(Boolean((_a = monitorFormRef.current) === null || _a === void 0 ? void 0 : _a.isValid()));
    }, [monitorFormRef]);
    return (React.createElement(SonarCard, { id: 'add-app', title: COPY_SPECIFIC.ADD_A_NEW_APP },
        React.createElement(Row, null,
            React.createElement(AddAppSettings, { applicationName: (_a = app === null || app === void 0 ? void 0 : app.name) !== null && _a !== void 0 ? _a : '', form: appSettingsForm, onFinish: handleOnFinish, onValuesChange: handleOnValuesChange, validator: validateNameLabelCombination }),
            React.createElement(MonitorFormList, { applicationId: applicationId, loading: Boolean(applicationId) && monitorData === undefined, monitors: initialMonitors, onValuesChange: handleOnMonitorValuesChange, ref: monitorFormRef })),
        React.createElement(Row, { className: 'add-app-buttons' },
            React.createElement(Button, { onClick: handleReturn }, COPY_SPECIFIC.CANCEL),
            React.createElement(Button, { disabled: !appSettingsForm.isValid || !monitorsFormIsValid, onClick: appSettingsForm.submit, type: 'primary' }, COPY_SPECIFIC.ADD_NEW_APP))));
};
